import type {
  PropertyFilterOperator,
  PropertyFilterQuery,
  PropertyFilterToken,
} from '@cloudscape-design/collection-hooks';
import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import type { SortingState } from '@risksmart-app/components/src/table/tableUtils';
import dayjs from 'dayjs';

import { isDateThresholdValue } from '../../../components/date-time-filter/dateFilterValue';
import { convertDateRangeValues } from '../../../pages/dashboards/widgets/filterHelpers';
import type { TableRecord, WhereFilter } from '../types';

const dotNotationToNestedKey = (dotNotation: string, value: unknown) => {
  const keys = dotNotation.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: Record<string, any> = {};
  let current = result;

  for (let i = 0; i < keys.length; i++) {
    if (i === keys.length - 1) {
      current[keys[i]] = value;
    } else {
      current[keys[i]] = {};
      current = current[keys[i]];
    }
  }

  return result;
};

export const sortingStateToGraphQLQuery = <T extends TableRecord>(
  sortingState: SortingState<T>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const key = sortingState.sortingColumn.sortingField ?? '';
  const value = sortingState.isDescending ? 'desc' : 'asc';

  return dotNotationToNestedKey(key, value);
};

const operatorGraphQLOperatorMap: {
  [operator: PropertyFilterOperator]: string;
} = {
  '=': '_eq',
  '!=': '_neq',
  ':': '_ilike',
  '!:': '_nilike',
  '>': '_gt',
  '<': '_lt',
  '>=': '_gte',
  '<=': '_lte',
};

const isDateRangePickerValue = (
  value: unknown
): value is DateRangePickerProps.Value => {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false;
  }

  return value.type === 'absolute' || value.type === 'relative';
};

const tokenToGraphQLFilter = ({
  propertyKey,
  operator,
  value,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
PropertyFilterToken): any => {
  if (!propertyKey) {
    return {};
  }

  if (isDateRangePickerValue(value)) {
    const { startDate, endDate } = convertDateRangeValues(value);
    if (!startDate || !endDate) {
      return {};
    }

    // Match the inclusive-day semantics of the client-side `match` function in
    // `dateRangeFilterOperators` — a row whose timestamp falls anywhere on the
    // start or end day must be included.
    return dotNotationToNestedKey(propertyKey, {
      _gte: dayjs(startDate).startOf('day').toISOString(),
      _lte: dayjs(endDate).endOf('day').toISOString(),
    });
  }

  // One-sided inclusive date filter (CUS-217). Rides the `=` operator, so without
  // this branch it would fall through to `_eq` with an object value. Bounded to
  // the whole local day to match `matchDateThreshold`.
  if (isDateThresholdValue(value)) {
    const day = dayjs(value.date);
    if (!day.isValid()) {
      return {};
    }

    return dotNotationToNestedKey(
      propertyKey,
      value.mode === 'onOrAfter'
        ? { _gte: day.startOf('day').toISOString() }
        : { _lte: day.endOf('day').toISOString() }
    );
  }

  let tokenValue = value;
  if (typeof tokenValue === 'string') {
    tokenValue = tokenValue.trim();
  }
  if (operator === ':' || operator === '!:') {
    tokenValue = `%${tokenValue}%`;
  }

  return dotNotationToNestedKey(propertyKey, {
    [operatorGraphQLOperatorMap[operator]]: tokenValue,
  });
};

export const propertyFilterToGraphQLQuery = <T extends TableRecord>(
  propertyFilter: PropertyFilterQuery
): WhereFilter<T> => {
  // Combine token filters by nesting through `_and` as Hasura/Drizzle expect
  // an array of sub-expressions. Single-token filters are returned flat —
  // wrapping in `_and: [...]` is unnecessary and the previous wrapper used an
  // empty object (`_and: {}`) which fails the tRPC Zod validation that
  // expects `_and` to be an array.
  //
  // `tokenToGraphQLFilter` returns `{}` for tokens it cannot serialise
  // (missing propertyKey, unparsable date range). Drop those before
  // composing so we never emit `_and: [{}, ...]`.
  const tokenFilters: WhereFilter<T>[] = propertyFilter.tokens
    .map(tokenToGraphQLFilter)
    .filter((filter) => Object.keys(filter).length > 0);

  const [first, ...rest] = tokenFilters;
  if (!first) {
    return {};
  }
  if (rest.length === 0) {
    return first;
  }

  const combined: WhereFilter<T> = {};
  combined._and = tokenFilters;

  return combined;
};
